import Axios from '@/utilities/axios-instance'
import navigation from '@/mixins/navigation'
import OFFER_STATUS from '@/constants/offer-status'
import DataLayer from '@/utilities/data-layer'

const baseURL = process.env.VUE_APP_LEAD_API_PERSONAL_LOANS_BASE_URL

export default {
  mixins: [navigation],
  computed: {
    $_offers_dataLayerObject () {
      return {
        event: `spring_${this.eventPrefix}_submit_application_continue`
      }
    },

    crm () {
      return this.$store.state.account.crm
    },

    odooToken () {
      return this.$route.query.token
    }
  },

  methods: {
    async $_offers_fetchOfferData (opportunityID, templateType = 'foundation-grad') {
      try {
        const requestURL = `${baseURL}odoo-get-opp/${opportunityID}`

        const { data } = await Axios.get(requestURL,
          {
            params: {
              template_type: templateType,
              access_id: this.odooToken
            }
          })

        const { opportunity, url } = data.data

        const {
          spring_uid: cduid,
          email_normalized: email,
          full_name: fullName,
          first_name: firstName,
          last_name: lastName,
          phone: homePhone,
          id: opportunityId,
          mailingStreet,
          mailingProvince,
          mailingPostalCode,
          mailingCity,
          estimatedInterest,
          annualInterestRate,
          annualInterestRateApr,
          loanNumber,
          totalLoanAmount,
          dateOfAdvance,
          firstPaymentDate,
          lastPaymentDate,
          paymentFrequency,
          loanPeriodInMonths,
          scheduledNoOfPayments,
          totalPaymentAmount,
          totalPaymentAmountPer,
          initialSecurity,
          bankNumber,
          bankAccount,
          bankTransit,
          amountPerScheduledPayment,
          status,
          linkExpiryDate,
          DBdata,
          bloomOptIn,
          bloomSubscription,
          bloomPayment,
          bloomTotalPayment,
          fundingDate,
          estimatedSecurityContribution,
          estimatedSecurityPaymentFrequencyContribution,
          monthlyMinimumPayment,
          totalMonthlyPayment,
          loanPaymentWithLPP,
          lppTotal,
          totalEstimatedLPP,
          financeCharge,
          totalOfPayments,
          fundDateFormatNew,
          loanAmount,
          premiumPerLoan,
          taxPerPayment,
          adminFee,
          creditMonitoring,
          letterOfDirectionData,
          amountToBorrower,
          debtConsolidationSum,
          isSplPrime,
          evergreenTopUp,
          prevSpringLoan
        } = opportunity

        const offerData = {
          opportunity: {
            id: opportunityID,
            fullName,
            firstName,
            lastName,
            homePhone,
            email,
            cduid,
            status,
            linkExpiryDate,
            address: {
              mailingStreet,
              mailingProvince,
              mailingPostalCode,
              mailingCity
            },
            bank: {
              bankNumber,
              bankAccount,
              bankTransit
            }
          },
          loan: {
            loanNumber,
            dateOfAdvance,
            firstPaymentDate,
            lastPaymentDate,
            estimatedInterest,
            totalLoanAmount,
            annualInterestRate,
            paymentFrequency,
            annualInterestRateApr,
            loanPeriodInMonths,
            scheduledNoOfPayments,
            totalPaymentAmount,
            totalPaymentAmountPer,
            initialSecurity,
            amountPerScheduledPayment,
            bloomOptIn,
            bloomSubscription,
            bloomPayment,
            bloomTotalPayment,
            loanPaymentWithLPP,
            lppTotal,
            totalEstimatedLPP,
            financeCharge,
            totalOfPayments,
            fundDateFormatNew,
            loanAmount,
            premiumPerLoan,
            taxPerPayment,
            adminFee,
            creditMonitoring,
            letterOfDirectionData,
            amountToBorrower,
            debtConsolidationSum,
            isSplPrime,
            evergreenTopUp,
            prevSpringLoan
          }
        }

        const contractData = {
          contractUrl: url,
          fundingDate,
          estimatedSecurityContribution,
          estimatedSecurityPaymentFrequencyContribution,
          monthlyMinimumPayment,
          totalMonthlyPayment
        }

        const bankingProvider = DBdata['banking_provider']
        const bankingSplitter = DBdata['banking_splitter']
        this.$store.dispatch('offers/setOfferData', offerData)
        this.$store.dispatch('setOppId', opportunityId)
        // if we are not using the FI selector, set the banking provider
        // else set the master banking provider for the FI Selector Screen
        if (process.env.VUE_APP_DISABLE_FI_SELECTOR) {
          this.$store.dispatch('setBankingProvider', bankingProvider)
        } else {
          this.$store.dispatch('setMasterBankingProvider', bankingProvider)
          this.$store.dispatch('setBankingSplitter', bankingSplitter)
        }
        this.$store.dispatch('offers/setOfferAgreementData', contractData)
        DataLayer.pushEvent('event', `spring_${this.eventPrefix}_accepted`)
        this.$_navigation_handleNext(this.$_offers_dataLayerObject)
      } catch (error) {
        console.error(error)
      }
    },

    async $_offers_acceptedOffer () {
      DataLayer.pushEvent('event', `spring_${this.eventPrefix}_accepted`)
      this.$_navigation_handleNext(this.$_offers_dataLayerObject)
    },

    async $_offers_refusedOffer () {
      try {
        const eContract = this.$store.state.offers.opportunity.id
        const requestURL = `${baseURL}odoo-update/${eContract}`

        const params = {
          status: OFFER_STATUS.REJECTED,
          access_id: this.odooToken
        }

        await Axios.patch(requestURL, params)
        DataLayer.pushEvent('event', `spring_${this.eventPrefix}_refused`)
        this.$store.dispatch('offers/setOfferStatus', OFFER_STATUS.REJECTED)
        this.$_navigation_handleNext(this.$_offers_dataLayerObject)
      } catch (error) {
        console.error(error)
      }
    },

    async $_offers_completeOffer (templateType = 'foundation-grad') {
      try {
        const eContract = this.$store.state.offers.opportunity.id
        if (this.crm === 'sf') return this.$_navigation_handleSorry()
        const requestURL = `${baseURL}odoo-update/${eContract}`
        const params = {
          status: OFFER_STATUS.COMPLETED,
          template_type: templateType,
          access_id: this.odooToken
        }

        await Axios.patch(requestURL, params)
        DataLayer.pushEvent('event', `spring_${this.eventPrefix}_signed`)
        this.$store.dispatch('offers/setOfferFinished')
      } catch (error) {
        console.error(error)
      }
    },

    async $_offers_submitFlinksData (eContract, loginId, flinksRequestId) {
      let formData = new FormData()
      formData.append('Login_Request_ID__c', loginId)
      formData.append('Flinks_Request_ID__c', flinksRequestId)
      formData.append('Opportunity__c', eContract)
      this.$_offers_submitBankData(formData, 'flinks')
    },

    async $_offers_submitPlaidData (eContract, messageData, publicToken) {
      let formData = new FormData()
      formData.append('Opportunity__c', eContract)
      formData.append('public_token', publicToken)
      formData.append('institution_id', messageData.institution_id)
      formData.append('institution_name', messageData.institution_name)
      formData.append('link_session_id', messageData.link_session_id)
      formData.append('request_id', messageData.request_id)
      formData.append('timestamp', messageData.timestamp)
      this.$_offers_submitBankData(formData, 'plaid')
    },

    async $_offers_submitBankData (formData, bankProvider) {
      try {
        if (this.crm === 'sf') return this.$_navigation_handleSorry()
        const requestURL = `${baseURL}odoo-documents`
        const { data } = await Axios.post(requestURL, formData)
        const { success } = data
        if (success) {
          DataLayer.pushEvent('event', `spring_${this.eventPrefix}_${bankProvider}-bank-submit`)
          this.$_navigation_handleNext(this.$_offers_dataLayerObject)
        }
      } catch (error) {
        console.error(error)
        alert('Oops! Something went wrong. Please call 1-888-865-6402 to finish your application')
      }
    }
  }
}
